import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import ReactTable from 'react-table';
import { Checkbox, FormControlLabel, MenuItem, TextField } from '@mui/material';
import { isEmpty, trim } from 'lodash';
import { isEqual } from 'lodash/lang';

import { withEuiFiltersProvider } from 'components/eui/EuiFiltersContext/EuiFiltersContext';
import ContractSpecialistModal from 'components/pages/study/StudyDetails/ContractSpecialistModal/ContractSpecialistModal';
import LeadModal from 'components/pages/study/StudyDetails/LeadModal/LeadModal';
import SsuOnHoldCheckbox from 'components/pages/study/StudyDetails/SiteDetails/SsuOnHoldCheckbox/SsuOnHoldCheckbox';
import { TravelStudyFolder } from 'components/pages/study/StudyDetails/TravelStudyFolder/TravelStudyFolder';

import { StudyApi } from '../../../../api';
import Common from '../../../../common/common';
import ModalBoxes from '../../../../common/feedback/ModalBoxes/ModalBoxes';
import Button from '../../../../common/general/Button';
import ButtonGroup from '../../../../common/general/ButtonGroup';
import Icon from '../../../../common/general/Icon';
import { withAppInfo } from '../../../../common/hocs/withAppInfo';
import NotificationManager from '../../../../common/notifications/NotificationManager';
import { STUDY_TIER_SAVED_SUCCESSFULLY } from '../../../../constants/notificationMessages';
import { protocolStatus } from '../../../../constants/protocolConstant';
import { CANCELED } from '../../../../constants/ssuStatuses';
import { STDT_REIMBURSEMENT, STTG_CTMS } from '../../../../constants/study/studyTypes';
import {
  EDIT_PM_LEAD_SPECIALIST_AND_CONTRACT_SPECIALIST,
  MANAGE_DIGITAL_PATIENT_PAYMENT,
  MANAGE_PATIENT_REIMBURSEMENT_SELF_SUBMISSION,
  MANAGE_STUDY_DETAILS,
  MANAGE_STUDY_ON_HOLD,
  UPDATE_STUDY_TIER,
  VIEW_STUDY_DETAILS,
  VIEW_STUDY_SITE_DETAILS
} from '../../../../constants/userOperations';
import { pendoTrackDefaultSortingChange } from '../../../../pendo/PendoUtils';
import { userHasAccessTo } from '../../../../services/auth';
import { onRequestDefaultError, onRequestError } from '../../../../services/handlers';
import { NS_COMMON, NSC_STUDY, NSPS_SITE, NSPS_STUDY } from '../../../../services/i18n/namespaces';
import { RT_TEXT_OVERWRITE } from '../../../../services/i18n/react-table';
import { setIn } from '../../../../services/immutable';
import { PageInfoHeader } from '../../../PageInfoHeader/PageInfoHeader';
import { generateUrlByKey } from '../../../root/router';

import FiltersSection from './FiltersSection/FiltersSection';
import InformedConsent from './InformedConsent/InformedConsent';
import PrintPatientEncountersGeneratorModal from './PrintPatientEncountersGeneratorModal/PrintPatientEncountersGeneratorModal';
import StudyTypeSetup from './StudyTypeSetup/StudyTypeSetup';
import StatusesSetup from './StatusesSetup';

import 'react-table/react-table.css';
import './StudyDetails.scss';

class StudyDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      studyIdentifier: null,
      SiteDetailsColumns: [],
      studyDetails: {},
      viewProtocolLink: null,
      typeParents: null,
      type: null,
      tiers: [],
      pmLead: null,
      selectedPmLead: null,
      pmLeadName: null,
      pmSpecialist: null,
      selectedPmSpecialist: null,
      pmSpecialistName: null,
      contractSpecialist: null
    };
  }

  showEncountersPrintVersionGenerator = () => {
    ModalBoxes.open({
      component: <PrintPatientEncountersGeneratorModal studyIdentifier={this.state.studyIdentifier} type={2} />
    });
  };

  onStudyTypeAndParentChange = (type, typeParents) => {
    this.setState({ type, typeParents });
  };

  onStudyChange = studyIdentifier => {
    const { t } = this.props;

    const onSsuOnHoldChange = (ssuId, onHold) => {
      this.setState(state => {
        return setIn(
          state,
          'studyDetails.listOfSites',
          state.studyDetails.listOfSites.map(site => {
            if (site.unique_identifier === ssuId) {
              return { ...site, onHold };
            }
            return site;
          })
        );
      });
    };

    StudyApi.getStudyTypes(studyIdentifier).then(({ data }) => {
      this.onStudyTypeAndParentChange(data.type, data.typeParents);
    }, onRequestError);

    StudyApi.getAllStudyTiers(studyIdentifier).then(({ data }) => {
      this.setState({ tiers: data });
    });

    if (studyIdentifier && !isEqual(studyIdentifier, this.state.studyIdentifier)) {
      this.setState({ studyIdentifier });
      StudyApi.populateStudyDetailsByIdentifier(studyIdentifier).then(res => {
        const SiteDetailsColumns = [
          {
            Header: t('site'),
            accessor: 'site_name',
            Cell: row => {
              return userHasAccessTo(VIEW_STUDY_SITE_DETAILS) ? (
                <NavLink
                  className="nav-link"
                  to={`/study-details/siteDetails/${row.row._original.unique_identifier}/${studyIdentifier}`}
                >
                  {row.row.site_name}
                </NavLink>
              ) : (
                row.row.site_name
              );
            },
            className: 'activelink'
          },
          {
            Header: t('siteNumber', { ns: NSPS_STUDY }),
            accessor: 'siteNumber',
            Cell: ({ value }) => (value && <span>{value}</span>) || '--'
          },
          {
            Header: t('status'),
            accessor: 'site_status',
            Cell: row => {
              return row.row.site_status ? <span> {row.row.site_status}</span> : '--';
            }
          },
          {
            Header: t('onHold', { ns: NSPS_SITE }),
            accessor: 'onHold',
            Cell: row => {
              return (
                <SsuOnHoldCheckbox
                  ssuId={row.row._original.unique_identifier}
                  onHold={row.row.onHold}
                  onChange={onHold => onSsuOnHoldChange(row.row._original.unique_identifier, onHold)}
                />
              );
            }
          },
          {
            Header: t('leadInvestigator', { ns: NSC_STUDY }),
            accessor: 'primary_investigator',
            Cell: row => {
              return row.row.primary_investigator ? <span> {row.row.primary_investigator}</span> : '--';
            }
          },
          {
            Header: t('preStudyVisit', { ns: NSC_STUDY }),
            accessor: 'psvDate',
            Cell: ({ value }) => (value && <span>{value}</span>) || '--',
            sortMethod: Common.dateComparator
          },
          {
            Header: t('sivDate', { ns: NSPS_STUDY }),
            accessor: 'sivDate',
            Cell: ({ value }) => (value && <span>{value}</span>) || '--',
            sortMethod: Common.dateComparator
          }
        ];
        const studyDetails = res?.data?.response || {};
        studyDetails.listOfSites = studyDetails.listOfSites?.filter(site => site?.site_status !== CANCELED) || [];

        const pmLead = res?.data?.response.pmLead ? res?.data?.response.pmLead : null;
        const pmLeadName = pmLead ? pmLead.name : null;
        const pmSpecialist = res?.data?.response.pmSpecialist ? res?.data?.response.pmSpecialist : null;
        const pmSpecialistName = pmSpecialist ? pmSpecialist.name : null;
        const contractSpecialist = res?.data?.response?.contractSpecialist;

        this.setState({
          studyDetails,
          SiteDetailsColumns,
          pmLead,
          pmLeadName,
          pmSpecialist,
          pmSpecialistName,
          contractSpecialist
        });
        this.buildViewProtocolLink(studyIdentifier, res.data.response.study_name);
      }, onRequestError);
    }
  };

  buildViewProtocolLink = studyId => {
    StudyApi.getProtocolsByStudyIdAndStatus(studyId, protocolStatus.active).then(res => {
      if (res.data && Array.isArray(res.data) && res.data.length > 0) {
        const activeProtocol = res.data.find(p => p.status === 'Active');
        const url = generateUrlByKey('Study Details.Source Data Preview', {
          studyId,
          protocolIdentity: activeProtocol.protocolId
        });
        this.setState({ viewProtocolLink: url });
      } else {
        this.setState({ viewProtocolLink: null });
      }
    }, onRequestError);
  };

  setPresentEpochs = () => {
    const isPresentEpochs = !this.state.studyDetails.presentEpochs;
    StudyApi.setIsPresentEpochs(this.state.studyDetails.unique_identifier, { isPresentEpochs }).then(({ data }) => {
      this.setState(state => {
        return { studyDetails: { ...state.studyDetails, presentEpochs: data } };
      });
    });
  };

  toggleSmsOptIn = () => {
    const smsOptIn = !this.state.studyDetails.smsOptIn;
    StudyApi.toggleSmsOptIn(this.state.studyDetails.unique_identifier, smsOptIn).then(({ data }) => {
      this.setState(state => {
        return { studyDetails: { ...state.studyDetails, smsOptIn: data } };
      });
    });
  };

  toggleOnHold = () => {
    const onHold = !this.state.studyDetails.onHold;
    StudyApi.toggleOnHold(this.state.studyDetails.unique_identifier, onHold).then(({ data }) => {
      this.setState(state => {
        return { studyDetails: { ...state.studyDetails, onHold: data } };
      });
    });
  };

  onStudyDetailsTravelFolderLinkUpdate = travelStudyFolder => {
    this.setState(prevState => {
      return { studyDetails: { ...prevState.studyDetails, travelStudyFolder } };
    });
  };

  onReimbursementSelfSubmissionEnabledUpdate = reimbursementSelfSubmissionEnabled => {
    StudyApi.updatePatientReimbursementSelfSubmission(
      this.state.studyDetails.unique_identifier,
      reimbursementSelfSubmissionEnabled
    ).then(({ data }) => {
      this.setState(state => {
        return { studyDetails: { ...state.studyDetails, reimbursementSelfSubmissionEnabled: data } };
      });
    });
  };

  onDigitalPatientPaymentUpdate = () => {
    const digitalPatientPayment = !this.state.studyDetails.digitalPatientPayment;
    StudyApi.updateDigitalPatientPayment(this.state.studyDetails.unique_identifier, digitalPatientPayment).then(
      ({ data }) => {
        this.setState(state => {
          return { studyDetails: { ...state.studyDetails, digitalPatientPayment: data } };
        });
      }
    );
  };

  onOptionalPatientSsnUpdate = () => {
    const optionalPatientSsn = !this.state.studyDetails.optionalPatientSsn;
    StudyApi.updateOptionalPatientSsn(this.state.studyDetails.unique_identifier, optionalPatientSsn).then(
      ({ data }) => {
        this.setState(state => {
          return { studyDetails: { ...state.studyDetails, optionalPatientSsn: data } };
        });
      }
    );
  };

  onStudyTierUpdate = tier => {
    StudyApi.updateStudyTier(this.state.studyDetails.unique_identifier, { tier })
      .then(({ data }) => {
        this.setState(state => {
          return { studyDetails: { ...state.studyDetails, tier: data } };
        });
        NotificationManager.success(STUDY_TIER_SAVED_SUCCESSFULLY);
      })
      .catch(err => onRequestDefaultError(err));
  };

  handleCostPerMileageUpdate = costPerMileage => {
    return StudyApi.updateCostPerMileage(this.state.studyDetails.unique_identifier, { costPerMileage }).then(
      ({ data }) => {
        this.setState(state => {
          return { studyDetails: { ...state.studyDetails, costPerMileage: costPerMileage } };
        });
      }
    );
  };

  preparePMLeadsAndSpecialists = currentLead => {
    const visitedPersonnelIds = new Set();

    const preparedList = this.state.studyDetails.studyPmTeamMembers.filter(user => {
      const hasRole = ['DS-AUTH-000013', 'DS-AUTH-000015'].includes(user.authority_identifier);

      if (!hasRole) return false;
      if (visitedPersonnelIds.has(user.personnelIdentifier)) return false;

      visitedPersonnelIds.add(user.personnelIdentifier);
      return true;
    });

    if (currentLead) {
      const unassignedSelect = {
        name: 'Unassigned'
      };
      preparedList.unshift(unassignedSelect);
    }
    return preparedList;
  };

  handlePMLeadChange = pmLead => {
    this.setState({ selectedPmLead: pmLead });
  };

  handlePMSpecialistChange = pmSpecialist => {
    this.setState({ selectedPmSpecialist: pmSpecialist });
  };

  openPmTeamMemberModal = (title, primaryPm, handleSpecialistChange, saveSpecialistChange) => {
    const specialists = this.preparePMLeadsAndSpecialists(primaryPm);
    ModalBoxes.open({
      title: title,
      component: (
        <LeadModal
          leadLabel={title}
          leadList={specialists}
          currentLead={primaryPm}
          handleLeadChange={handleSpecialistChange}
          saveLead={saveSpecialistChange}
        />
      )
    });
  };

  openContractSpecialistModal = (title, primaryPm, saveSpecialistChange) => {
    ModalBoxes.open({
      title: title,
      component: <ContractSpecialistModal leadLabel={title} currentLead={primaryPm} saveLead={saveSpecialistChange} />
    });
  };

  savePmLead = () => {
    const pmLeadId = this.state.selectedPmLead ? this.state.selectedPmLead.personnelIdentifier : null;
    const studyId = this.state.studyIdentifier;
    const pmLeadRequest = { personnelId: pmLeadId };

    StudyApi.updatePmLead(studyId, pmLeadRequest).then(resp => {
      StudyApi.populateStudyDetailsByIdentifier(studyId).then(({ data: { response } }) => {
        const pmLead = response.pmLead ? response.pmLead : null;
        const pmLeadName = pmLead ? pmLead.name : null;
        this.setState({ pmLead, pmLeadName });
      }, onRequestDefaultError);
    }, onRequestDefaultError);
  };

  savePmSpecialist = () => {
    const pmSpecialistId = this.state.selectedPmSpecialist ? this.state.selectedPmSpecialist.personnelIdentifier : null;
    const studyId = this.state.studyIdentifier;
    const pmSpecialistRequest = { personnelId: pmSpecialistId };

    StudyApi.updatePmSpecialist(studyId, pmSpecialistRequest).then(resp => {
      StudyApi.populateStudyDetailsByIdentifier(studyId).then(({ data: { response } }) => {
        const pmSpecialist = response.pmSpecialist ? response.pmSpecialist : null;
        const pmSpecialistName = pmSpecialist ? pmSpecialist.name : null;
        this.setState({ pmSpecialist, pmSpecialistName });
      }, onRequestDefaultError);
    }, onRequestDefaultError);
  };

  saveContractSpecialist = value => {
    const contractSpecialist = isEmpty(trim(value)) ? null : trim(value);
    const studyId = this.state.studyIdentifier;
    const contractSpecialistRequest = { specialistName: contractSpecialist };

    StudyApi.updateContractSpecialist(studyId, contractSpecialistRequest).then(resp => {
      StudyApi.populateStudyDetailsByIdentifier(studyId).then(({ data: { response } }) => {
        const contractSpecialist = response.contractSpecialist ? response.contractSpecialist : null;
        this.setState({ contractSpecialist });
      }, onRequestDefaultError);
    }, onRequestDefaultError);
  };

  render({ props, state, onStudyChange } = this) {
    const { t } = props;
    const { studyDetails, viewProtocolLink, typeParents, type, tiers } = state;
    const divStyle = { minHeight: '444px', border: '1px solid #dee2e6', marginTop: '15px', paddingTop: '146px' };

    const isStudyTypeCTMS = type === STTG_CTMS;
    const isStudyReimbursementOption = typeParents
      ?.find(e => e.typeParent === STTG_CTMS)
      .types?.find(e => e.name === STDT_REIMBURSEMENT)?.selected;

    return (
      <div className="studySiteDetails footpadb iefixflex">
        <PageInfoHeader
          right={
            <ButtonGroup>
              <Button size="h28" priority="medium" to={viewProtocolLink || ''} disabled={!viewProtocolLink}>
                <Icon>visibility</Icon>
                {t('viewProtocolSetup', { ns: NSPS_STUDY })}
              </Button>
              <Button size="h28" priority="medium" onClick={this.showEncountersPrintVersionGenerator}>
                <Icon>print</Icon>
                {t('printEncounterForms', { ns: NSPS_STUDY })}
              </Button>
            </ButtonGroup>
          }
        >
          <div className="general-header-group-container general-header-wrapper">
            <FiltersSection onStudyChange={onStudyChange} />
          </div>
        </PageInfoHeader>
        <section>
          {Object.keys(studyDetails).length === 0 ? (
            <div style={divStyle} align="center">
              <div className="col-md-3 text-center mt-5 custummsg">{t('pleaseSelectTheStudy', { ns: NSPS_STUDY })}</div>
            </div>
          ) : (
            <div className="p-3 m-0 mt-2 border row justify-content-between">
              <div className="row border-bottom-dotted col-12 m-0 p-0 justify-content-between mb-3">
                <h5 className="pt-2 c-p">
                  {t('studyDetails', { ns: NSPS_STUDY })}:{' '}
                  <span className="text-muted bluetxt">{studyDetails.study_name}</span>
                </h5>
              </div>
              <div className="row">
                <div className="col-2">
                  <div className="form-group">
                    <label htmlFor="site">{t('name')}</label>
                    <div className="input-group">
                      <input type="text" className="form-control" value={studyDetails.study_name || ''} disabled />
                    </div>
                  </div>
                </div>
                <div className="col-2">
                  <div className="form-group">
                    <label htmlFor="site">{t('phase', { ns: NSPS_STUDY })}</label>
                    <div className="input-group">
                      <input type="text" className="form-control" value={studyDetails.study_phase || ''} disabled />
                    </div>
                  </div>
                </div>
                <div className="col-2">
                  <div className="form-group">
                    <label htmlFor="site">{t('projectCode', { ns: NSPS_STUDY })}</label>
                    <div className="input-group">
                      <input type="text" className="form-control" value={studyDetails.project_code || ''} disabled />
                    </div>
                  </div>
                </div>
                <div className="col-2">
                  <div className="form-group">
                    <label htmlFor="site">{t('therapeuticArea', { ns: NSPS_STUDY })}</label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        value={studyDetails.therapeutic_area || ''}
                        disabled
                      />
                    </div>
                  </div>
                </div>
                <div className="col-2">
                  <div className="form-group">
                    <label htmlFor="site">{t('customer', { ns: NSPS_STUDY })}</label>
                    <div className="input-group">
                      <input type="text" className="form-control" value={studyDetails.customer || ''} disabled />
                    </div>
                  </div>
                </div>
                <div className="col-2">
                  <div className="form-group">
                    <label htmlFor="site">{t('sponsor', { ns: NSPS_STUDY })}</label>
                    <div className="input-group">
                      <input type="text" className="form-control" value={studyDetails.sponsor || ''} disabled />
                    </div>
                  </div>
                </div>
                <div className="col-2">
                  <div className="form-group">
                    <label htmlFor="site">{t('status')}</label>
                    <div className="input-group">
                      <input type="text" className="form-control" value={studyDetails.status || ''} disabled />
                    </div>
                  </div>
                </div>
                <div className="col-2 col-half-offset">
                  <div className="form-group">
                    <label htmlFor="site">{t('screenFailRate', { ns: NSPS_STUDY })} (%)</label>
                    <div className="input-group">
                      <input type="text" value={studyDetails.screen_failure_rate} className="form-control" disabled />
                    </div>
                  </div>
                </div>
                <div className="col-2 col-half-offset">
                  <div className="form-group">
                    <label htmlFor="site">{t('dropRate', { ns: NSPS_STUDY })} (%)</label>
                    <div className="input-group">
                      <input type="text" value={studyDetails.drop_out_rate} className="form-control" disabled />
                    </div>
                  </div>
                </div>
                <div className="col-2">
                  <div className="form-group">
                    <label htmlFor="site">{t('cro', { ns: NSPS_STUDY })}</label>
                    <div className="input-group">
                      <input type="text" className="form-control" value={studyDetails.cro || ''} disabled />
                    </div>
                  </div>
                </div>
                <div className="col-2">
                  <div className="form-group">
                    <div className="checkboxes">
                      <div>
                        <label htmlFor="nonTraditional">{t('nonTraditional', { ns: NSPS_STUDY })}</label>
                        <div className="input-group" style={{ marginTop: '0.375rem' }}>
                          <Checkbox disabled checked={studyDetails.nonTraditional} />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="presentEpochs">{t('presentEpochs', { ns: NSPS_STUDY })}</label>
                        <div className="input-group" style={{ marginTop: '0.375rem' }}>
                          <Checkbox
                            disabled={!userHasAccessTo(MANAGE_STUDY_DETAILS)}
                            checked={studyDetails.presentEpochs}
                            onChange={this.setPresentEpochs}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-2">
                  <div className="form-group">
                    <label htmlFor="site">{t('indication', { ns: NS_COMMON })}</label>
                    <div className="input-group">
                      <input type="text" className="form-control" value={studyDetails.indication || ''} disabled />
                    </div>
                  </div>
                </div>
                <div className="col-2">
                  <div className="form-group">
                    <div className="checkboxes">
                      <div>
                        <label htmlFor="smsOptIn">{t('smsOptIn', { ns: NSPS_STUDY })}</label>
                        <div className="input-group" style={{ marginTop: '0.375rem' }}>
                          <Checkbox
                            disabled={!userHasAccessTo(MANAGE_STUDY_DETAILS)}
                            checked={studyDetails.smsOptIn}
                            onChange={this.toggleSmsOptIn}
                          />
                        </div>
                      </div>
                      {isStudyTypeCTMS && isStudyReimbursementOption && (
                        <FormControlLabel
                          value="top"
                          control={
                            <Checkbox
                              checked={studyDetails.reimbursementSelfSubmissionEnabled}
                              disabled={!userHasAccessTo(MANAGE_PATIENT_REIMBURSEMENT_SELF_SUBMISSION)}
                              onChange={({ target }) => this.onReimbursementSelfSubmissionEnabledUpdate(target.checked)}
                            />
                          }
                          label={t('patientSelfSubmissionOfReimbursement', { ns: NSPS_STUDY })}
                          labelPlacement="top"
                          sx={{ alignItems: 'start', justifyContent: 'space-between', marginBottom: '0px' }}
                          slotProps={{
                            typography: {
                              sx: { fontSize: '.9rem' }
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-2">
                  <div className="form-group">
                    <div className="checkboxes">
                      {this.props.appInfo.features.digitalPatientPaymentEnabled && (
                        <div>
                          <label htmlFor="digitalPatientPayment">
                            {t('digitalPatientPayment', { ns: NSPS_STUDY })}
                          </label>
                          <div className="input-group" style={{ marginTop: '0.375rem' }}>
                            <Checkbox
                              disabled={!userHasAccessTo(MANAGE_DIGITAL_PATIENT_PAYMENT)}
                              checked={studyDetails.digitalPatientPayment}
                              onChange={this.onDigitalPatientPaymentUpdate}
                            />
                          </div>
                        </div>
                      )}
                      {this.props.appInfo.features.optionalSsnEnabled &&
                        this.props.appInfo.features.digitalPatientPaymentEnabled &&
                        studyDetails.digitalPatientPayment && (
                          <div>
                            <label htmlFor="optionalPatientSsn">
                              {t('optionalSsnForDigitalPayments', { ns: NSPS_STUDY })}
                            </label>
                            <div className="input-group" style={{ marginTop: '0.375rem' }}>
                              <Checkbox
                                disabled={!userHasAccessTo(MANAGE_DIGITAL_PATIENT_PAYMENT)}
                                checked={studyDetails.optionalPatientSsn}
                                onChange={this.onOptionalPatientSsnUpdate}
                              />
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
                {userHasAccessTo(VIEW_STUDY_DETAILS) && (
                  <div className="col-2">
                    <TextField
                      fullWidth
                      id="study-tier-select"
                      select
                      label={t('studyTier', { ns: NSPS_STUDY })}
                      value={this.state.studyDetails.tier || ''}
                      onChange={event => this.onStudyTierUpdate(event.target.value)}
                      disabled={!userHasAccessTo(UPDATE_STUDY_TIER)}
                    >
                      {tiers.map(tier => {
                        return (
                          <MenuItem key={tier} value={tier}>
                            {t(`tiersMap.${tier}`, { ns: NSPS_STUDY })}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  </div>
                )}
                <div className="col-2">
                  <div className="form-group">
                    <div className="checkboxes">
                      <div>
                        <label htmlFor="onHold">{t('onHold', { ns: NSPS_STUDY })}</label>
                        <div className="input-group" style={{ marginTop: '0.375rem' }}>
                          <Checkbox
                            disabled={!userHasAccessTo(MANAGE_STUDY_ON_HOLD)}
                            checked={studyDetails.onHold}
                            onChange={this.toggleOnHold}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <TravelStudyFolder
                  studyDetails={state.studyDetails}
                  onStudyDetailsTravelFolderLinkUpdate={this.onStudyDetailsTravelFolderLinkUpdate}
                  isStudyTypeCTMS={isStudyTypeCTMS}
                />
                <div className="row col-12 m-0 px-3 p-1 ">
                  <div className="col-md-3 col-half-offset">
                    <div className="form-group" data-testid="pm-lead-field">
                      <label htmlFor="site">{t('pmLead', { ns: NSPS_SITE })}</label>
                      <div className="input-group">
                        <span className="col-9 mr-0 p-0">
                          <input
                            type="text"
                            className="form-control"
                            value={this.state.pmLeadName ? this.state.pmLeadName : ''}
                            disabled
                          />
                        </span>
                        <span className="col-3 pt-1 pr-0">
                          {userHasAccessTo(EDIT_PM_LEAD_SPECIALIST_AND_CONTRACT_SPECIALIST) ? (
                            <i
                              className="glyphicon glyphicon-edit pr-2 editicn"
                              role="button"
                              tabIndex={0}
                              aria-label="Edit PM Lead"
                              data-testid="pm-lead-edit-icon"
                              onClick={() =>
                                this.openPmTeamMemberModal(
                                  t('pmLead', { ns: NSPS_SITE }),
                                  state.pmLead,
                                  this.handlePMLeadChange,
                                  this.savePmLead
                                )
                              }
                            />
                          ) : (
                            ''
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 col-half-offset">
                    <div className="form-group" data-testid="pm-specialist-field">
                      <label htmlFor="site">{t('pmSpecialist', { ns: NSPS_SITE })}</label>
                      <div className="input-group">
                        <span className="col-9 mr-0 p-0">
                          <input
                            type="text"
                            className="form-control"
                            value={this.state.pmSpecialistName ? this.state.pmSpecialistName : ''}
                            disabled
                          />
                        </span>
                        <span className="col-3 pt-1 pr-0">
                          {userHasAccessTo(EDIT_PM_LEAD_SPECIALIST_AND_CONTRACT_SPECIALIST) ? (
                            <i
                              className="glyphicon glyphicon-edit pr-2 editicn"
                              role="button"
                              tabIndex={0}
                              aria-label="Edit PM Specialist"
                              data-testid="pm-specialist-edit-icon"
                              onClick={() =>
                                this.openPmTeamMemberModal(
                                  t('pmSpecialist', { ns: NSPS_SITE }),
                                  state.pmSpecialist,
                                  this.handlePMSpecialistChange,
                                  this.savePmSpecialist
                                )
                              }
                            />
                          ) : (
                            ''
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 col-half-offset">
                    <div className="form-group" data-testid="contract-specialist-field">
                      <label htmlFor="site">{t('contractSpecialist', { ns: NSPS_SITE })}</label>
                      <div className="input-group">
                        <span className="col-9 mr-0 p-0">
                          <input
                            type="text"
                            className="form-control"
                            value={this.state.contractSpecialist ? this.state.contractSpecialist : ''}
                            disabled
                          />
                        </span>
                        <span className="col-3 pt-1 pr-0">
                          {userHasAccessTo(EDIT_PM_LEAD_SPECIALIST_AND_CONTRACT_SPECIALIST) ? (
                            <i
                              className="glyphicon glyphicon-edit pr-2 editicn"
                              role="button"
                              tabIndex={0}
                              aria-label="Edit Contract Specialist"
                              data-testid="contract-specialist-edit-icon"
                              onClick={() =>
                                this.openContractSpecialistModal(
                                  t('contractSpecialist', { ns: NSPS_SITE }),
                                  state.contractSpecialist,
                                  this.saveContractSpecialist
                                )
                              }
                            />
                          ) : (
                            ''
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row col-12 m-0 px-3 p-1 ">
                  <div className="col-md-12 row m-0 p-0 mb-2 pb-0 border-bottom-dotted justify-content-between">
                    <h5 className="mt-0 col-md-8 col-sm-auto p-0 c-p ">{t('siteDetails', { ns: NSPS_STUDY })}</h5>
                  </div>
                  <div className="col-12 table-border p-0">
                    <ReactTable
                      {...RT_TEXT_OVERWRITE}
                      data={this.state.studyDetails.listOfSites}
                      columns={this.state.SiteDetailsColumns}
                      minRows={1}
                      multiSort
                      noDataText={t('noRecordFound')}
                      className="table table-responsive-sm activity-table mb-0"
                      showPagination
                      onSortedChange={pendoTrackDefaultSortingChange}
                      nextText=">>"
                      previousText="<<"
                      pageSizeOptions={[25, 50, 100]}
                      defaultPageSize={25}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
        <StatusesSetup
          studyId={this.state.studyIdentifier}
          status={this.state.studyDetails.status}
          studyName={studyDetails.study_name}
        />
        <InformedConsent studyId={studyDetails.unique_identifier} studyName={studyDetails.study_name} />
        <StudyTypeSetup
          studyDetails={studyDetails}
          handleCostPerMileageUpdate={this.handleCostPerMileageUpdate}
          onStudyTypeAndParentChange={this.onStudyTypeAndParentChange}
        />
      </div>
    );
  }
}

export default withAppInfo(withEuiFiltersProvider(withTranslation()(StudyDetails), { cachePrefix: 'SDF' }));
